import * as React from 'react';
import { Box, styled, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useResponsive } from 'src/hooks/useResponsive';
import { useSelector } from 'react-redux';
import { ApplicationState } from 'store/store';
import { isDefined, isEmptyString, isNotNull } from 'lib/typeInference';
import { useQuery } from 'src/hooks/useQuery';
import { MenuSearchButton } from 'components/menu/MenuSearchButton';
import { SuggestionsProvider } from 'src/hooks/useSuggestionsContext';
import { MenuSearch } from 'components/menu/MenuSearchDialog';

const PREFIX = 'DineInTimeslotCard';

const classes = {
    lineHeightReset: `${PREFIX}lineHeightReset`
};

const StyledBox = styled(Box, {
    shouldForwardProp: prop => prop !== 'isDesktop'
})<{ isDesktop: boolean }>(({ theme, isDesktop }) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    backgroundColor: theme.palette.titleTextColour,
    borderRadius: isDesktop ? theme.shape.borderRadius : 0,
    [`& .${classes.lineHeightReset}`]: {
        lineHeight: '1'
    }
}));

const StyledMenuCollectionTitle = styled(Typography)(({ theme }) => ({
    fontSize: '1.125rem',
    lineHeight: '1',
    fontWeight: 700,
    color: theme.palette.background.default
}));

const StyledTitleText = styled(Typography)(({ theme }) => ({
    color: theme.palette.background.default,
    lineHeight: '1',
    // line clamped at 1
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: '1',
    WebkitBoxOrient: 'vertical',
    textWrap: 'wrap'
}));

export const DineInPrepTimeCard = () => {
    const { t } = useTranslation();
    const { isDesktop } = useResponsive();
    const { settings } = useSelector((state: ApplicationState) => state.settings);
    const { prepTimePadding } = useSelector((state: ApplicationState) => state.basket);
    const currentSearch = useQuery('search');

    const [openMenuSearch, setOpenMenuSearch] = React.useState(() => {
        if (isNotNull(currentSearch) && isDefined(currentSearch) && !isEmptyString(currentSearch)) {
            return true;
        }
        return false;
    });

    const description = React.useMemo(() => {
        const timePadding = prepTimePadding ?? 0;
        return `${t('ORDER_STATUS_ORDER_READY_IN')} ${t('ORDER_STATUS_ORDER_READY_IN_MINS', {
            from: (settings?.preOrder?.readyPeriodStart || 5) + timePadding,
            to: (settings?.preOrder?.readyPeriodEnd || 20) + timePadding
        })}`;
    }, [t, settings?.preOrder?.readyPeriodStart, settings?.preOrder?.readyPeriodEnd, prepTimePadding]);

    const handleCloseMenuSearch = React.useCallback(() => {
        setOpenMenuSearch(false);
    }, []);
    const handleOpenMenuSearch = React.useCallback(() => {
        setOpenMenuSearch(true);
    }, []);

    return (
        <StyledBox isDesktop={isDesktop}>
            <Box display="flex" flexDirection="column" flex={1} paddingY={2} paddingX={2}>
                <StyledMenuCollectionTitle>{t('MENU_COLLECTION')}</StyledMenuCollectionTitle>
                <Box display="flex" alignItems="center" className={classes.lineHeightReset}>
                    <Box>
                        <StyledTitleText variant="caption">{description}</StyledTitleText>
                    </Box>
                </Box>
            </Box>
            <MenuSearchButton handleOpenMenuSearch={handleOpenMenuSearch} />
            {!!openMenuSearch && (
                <SuggestionsProvider>
                    <MenuSearch open={openMenuSearch} onClose={handleCloseMenuSearch} />
                </SuggestionsProvider>
            )}
        </StyledBox>
    );
};
